import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePrivileges from '../../../Auth/usePrivileges';
import useAlert from '../../../Alerts/useAlert';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import CommentDialog from '../../../Dialogs/CommentDialog';
import { useDateUtils } from '../../../Utilities/useDateUtils';
import { LotRevision } from '../../../Types/LotRevision';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { Button } from 'react-bootstrap';

export interface LotRevisionComponentProps {
    setLoading: Dispatch<SetStateAction<boolean>>,
}

function LotRevisionComponent({ setLoading }: LotRevisionComponentProps) {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { convertUTCToMedDateTimeSecs } = useDateUtils();
    const { lotDetail: { summary: { lotNumber, businessUnitCode, lotRevisionId, latestRevisionId }, revisions }, getLotDetail } = useContext(LotDetailContext);
    const { admin } = usePrivileges(businessUnitCode);

    const [showChangeLotRevisionCommentDialog, setShowChangeLotRevisionCommentDialog] = useState(false);

    // Get revision date to show in the summary so it is obvious we are not viewing the latest
    const getRevisionDate = () => {
        let revision = revisions.find((x: LotRevision) => x.lotRevisionId === lotRevisionId);

        return convertUTCToMedDateTimeSecs(revision?.revisionDateUtc);
    }

    // Check we are viewing the latest revision of the lot
    const isLatestRevision = () => {
        return lotRevisionId === latestRevisionId;
    }

    const changeLotRevision = (comment: string) => {

        const revisionId = lotRevisionId;

        let url = `api/v1/lot/${lotNumber}/revision`;
        if (revisionId) {
            url += `?revisionId=${revisionId}`;
        }

        setLoading(true);

        fetchDataAuthenticated(url, 'PUT', JSON.stringify(comment))
            .then(response => {
                if (response.ok) {
                    setAlert("success", "Success", `Changed lot ${lotNumber} to revision ${revisionId}`);
                    getLotDetail();
                } else {
                    throw new Error("Failed to change the lot revision.");
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    }

    return (
        <>
            <div className="d-flex align-items-center me-2">
                {!isLatestRevision() &&
                    <span className="me-2">{t<string>('LotDetails.Summary.ViewingRevision')} {getRevisionDate()}</span>
                }
                {admin && !isLatestRevision() &&
                    <Button variant="link" onClick={() => setShowChangeLotRevisionCommentDialog(true)}>
                        {t<string>('LotDetails.Summary.SetAsLatestRevision')}
                    </Button>
                }
            </div>
            <CommentDialog showDialog={showChangeLotRevisionCommentDialog} closeDialog={() => setShowChangeLotRevisionCommentDialog(false)} onSave={changeLotRevision} />
        </>
    );
}

export default LotRevisionComponent;